import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, inject, NgZone, PLATFORM_ID, signal } from '@angular/core';

/** Same breakpoint as the layout's CSS (`@media (max-width: 844px)`) */
const MOBILE_BREAKPOINT = 844;

@Injectable({
	providedIn: 'root',
})
export class ViewportService {
	private readonly platformId = inject(PLATFORM_ID);
	private readonly mobile = signal(false);

	/** Mobile viewport (width <= 844px), as a signal; false in SSR */
	readonly isMobile = this.mobile.asReadonly();

	constructor() {
		if (!isPlatformBrowser(this.platformId)) return;

		const zone = inject(NgZone);
		this.mobile.set(this.measure());

		// Resize fires dozens of times per second: listen outside Angular and only re-enter (one change
		// detection) when the width crosses the breakpoint
		const onResize = () => {
			const mobile = this.measure();
			if (mobile !== this.mobile()) zone.run(() => this.mobile.set(mobile));
		};
		zone.runOutsideAngular(() => window.addEventListener('resize', onResize, { passive: true }));
		inject(DestroyRef).onDestroy(() => window.removeEventListener('resize', onResize));
	}

	private measure(): boolean {
		return window.innerWidth <= MOBILE_BREAKPOINT;
	}

	/**
	 * Returns whether the current viewport is mobile.
	 * Can be used to adapt components based on screen size.
	 * In SSR, defaults to false.
	 */
	isMobileView(): boolean {
		return this.mobile();
	}
}
